import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'deliveryLog';

// Action Types
export const types = {
  SEARCH_TRANSACTIONS: createAsyncTypes(`${KEY}/SEARCH_TRANSACTIONS`),
  RESET_DELIVERY_LOG: `${KEY}/RESET_DELIVERY_LOG`,
  RESET_EXPORT_DATA: `${KEY}/RESET_EXPORT_DATA`,
  GET_TRANSACTION_PRICE_TYPES: createAsyncTypes(`${KEY}/GET_TRANSACTION_PRICE_TYPES`),
  REFUND_TRANSACTION: createAsyncTypes(`${KEY}/REFUND_TRANSACTION`),
  VOID_TRANSACTION: createAsyncTypes(`${KEY}/VOID_TRANSACTION`),
  RECONCILE_TRANSACTIONS: createAsyncTypes(`${KEY}/RECONCILE_TRANSACTIONS`),
  GET_SETTLEMENT_REPORT: createAsyncTypes(`${KEY}/GET_SETTLEMENT_REPORT`),
  GET_SALES_REPORT: createAsyncTypes(`${KEY}/GET_SALES_REPORT`),
  GET_SALES_BY_CARD_TYPE_REPORT: createAsyncTypes(`${KEY}/GET_SALES_BY_CARD_TYPE_REPORT`),
  RESET_SALES_BY_CARD_TYPE_REPORT: `${KEY}/RESET_SALES_BY_CARD_TYPE_REPORT`,
  RESET_SALES_REPORT: `${KEY}/RESET_SALES_REPORT`,
  RESET_SETTLEMENT_REPORT: `${KEY}/RESET_SETTLEMENT_REPORT`
};

export interface Transaction {
  orderTime: string;
  id: string;
  kioskId: string;
  kioskName: string;
  areaId: string;
  areaName: string;
  venueId: string;
  venueName: string;
  flow: string;
  deliveryType: string;
  integrationOrderId: string;
  ticketCount: number;
  deliveryFailedTicketCount: number;
  amount: number;
  status: number;
  cardholderName?: string;
  cardType?: string;
  cardBin?: string;
  cardLastFour?: string;
  cardExpiry?: string;
  paymentGatewayCode?: string;
  paymentGatewaySaleReferenceNumber?: string;
  paymentGatewayRefundReferenceNumber?: string;
  paymentGatewayApprovedAmount?: number;
  transactionFee1Amount?: number;
  transactionFee1PercentageAmount?: number;
  venueTax1Label?: string;
  venueTax1PercentageAmount?: number;
  soldAt: string;
  reversedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SalesDetails {
  taxesDeducted: number;
  venueTax1: number;
  amount: number;
  /** Only included for Global Admin */
  cardProcessingFeeMarkup?: number;
  /** Only included for Global Admin */
  transactionFee1?: number;
  /** Only included for Global Admin */
  productFees?: number;
  kisFees: number;
  kisFeesDeducted: number;
}

export interface KioskSalesDetails extends SalesDetails {
  kioskName: string;
  venueName: string;
  areaName: string;
}

export interface KioskSalesSummary {
  kioskName: string;
  venueName: string;
  areaName: string;
  amount: number;
  transactionCount: number;
}

interface TotalKioskSalesSummary {
  amount: number;
  transactionCount: number;
}

export interface GetSalesReportResponse {
  data: KioskSalesSummary[];
  total: TotalKioskSalesSummary;
}

export interface GetSalesReportRequest {
  type: typeof types.GET_SALES_REPORT.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
  };
}

export interface GetSalesReportPending {
  type: typeof types.GET_SALES_REPORT.PENDING;
  payload: boolean;
}

export interface GetSalesReportSuccess {
  type: typeof types.GET_SALES_REPORT.SUCCESS;
  payload: GetSalesReportResponse;
}

export interface GetSalesReportError {
  type: typeof types.GET_SALES_REPORT.ERROR;
  payload: string;
}

export interface KioskSalesByCardType {
  kioskName: string;
  amexTransactionsCount: number;
  amexTransactionsAmount: number;
  nonAmexTransactionsCount: number;
  nonAmexTransactionsAmount: number;
}

export interface GetSalesByCardTypeReportResponse {
  data: KioskSalesByCardType[];
}

export interface GetSalesByCardTypeReportRequest {
  type: typeof types.GET_SALES_BY_CARD_TYPE_REPORT.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
  };
}

export interface GetSalesByCardTypeReportPending {
  type: typeof types.GET_SALES_BY_CARD_TYPE_REPORT.PENDING;
  payload: boolean;
}

export interface GetSalesByCardTypeReportSuccess {
  type: typeof types.GET_SALES_BY_CARD_TYPE_REPORT.SUCCESS;
  payload: GetSalesByCardTypeReportResponse;
}

export interface GetSalesByCardTypeReportError {
  type: typeof types.GET_SALES_BY_CARD_TYPE_REPORT.ERROR;
  payload: string;
}

export interface DeliveryLogState {
  readonly data: Transaction[];
  readonly hasMore: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  readonly totalRecords: number;
  readonly priceTypes: TransactionPriceType[];
  readonly priceTypesPending: boolean;
  readonly settlementReport: {
    data: KioskSalesDetails[] | null;
    total: SalesDetails | null;
    pending: boolean;
    error: string | null;
  };
  readonly salesReport: {
    data: KioskSalesSummary[] | null;
    total: TotalKioskSalesSummary | null;
    pending: boolean;
    error: string | null;
  };
  readonly salesByCardTypeReport: {
    data: KioskSalesByCardType[] | null;
    pending: boolean;
    error: string | null;
  };
  readonly export: {
    readonly data: Transaction[];
    readonly pending: boolean;
  };
}

export interface SearchTransactionsRequest {
  type: typeof types.SEARCH_TRANSACTIONS.REQUEST;
  payload: {
    page?: number;
    sort?: string;
    sortColumn?: string;
    startTime?: string | null;
    endTime?: string | null;
    flows?: string[];
    deliveryTypes?: string[];
    exportData?: boolean;
    searchValue?: string;
    clientIntegrationId: string;
  };
}

export interface TransactionPriceType {
  id: string;
  transactionId: string;
  productId: string;
  productName: string;
  priceTypeId: string;
  priceTypeName: string;
  priceTypePrice: string | null;
  productFee1Amount: string | null;
  productFee1PercentageAmount: string | null;
}

export interface GetSettlementReportResponse {
  data: KioskSalesDetails[];
  total: SalesDetails;
}

export interface GetSettlementReportRequest {
  type: typeof types.GET_SETTLEMENT_REPORT.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
  };
}

export interface GetSettlementReportPending {
  type: typeof types.GET_SETTLEMENT_REPORT.PENDING;
  payload: boolean;
}

export interface GetSettlementReportSuccess {
  type: typeof types.GET_SETTLEMENT_REPORT.SUCCESS;
  payload: GetSettlementReportResponse;
}

export interface GetSettlementReportError {
  type: typeof types.GET_SETTLEMENT_REPORT.ERROR;
  payload: string;
}

export interface SearchTransactionsError {
  type: typeof types.SEARCH_TRANSACTIONS.ERROR;
  payload: string;
}

export interface SearchTransactionsPending {
  type: typeof types.SEARCH_TRANSACTIONS.PENDING;
  payload: { pending: boolean; exportData: boolean };
}

export interface SearchTransactionsSuccess {
  type: typeof types.SEARCH_TRANSACTIONS.SUCCESS;
  payload: {
    data: Transaction[];
    total: number;
    exportData: boolean;
  };
}

export interface RefundTransactionSuccess {
  type: typeof types.REFUND_TRANSACTION.SUCCESS;
  payload: Transaction;
}

export interface VoidTransactionSuccess {
  type: typeof types.VOID_TRANSACTION.SUCCESS;
  payload: Transaction;
}

export interface SearchTransactionsResponse {
  data: Transaction[];
  total: number;
}

export interface ResetDeliveryLog {
  type: typeof types.RESET_DELIVERY_LOG;
}

export interface GetTransactionPriceTypesRequest {
  type: typeof types.GET_TRANSACTION_PRICE_TYPES.REQUEST;
  payload: {
    transactionId: string;
    clientIntegrationId: string;
  };
}

export interface GetTransactionPriceTypesSuccess {
  type: typeof types.GET_TRANSACTION_PRICE_TYPES.SUCCESS;
  payload: TransactionPriceType[];
}

export interface GetTransactionPriceTypesPending {
  type: typeof types.GET_TRANSACTION_PRICE_TYPES.PENDING;
  payload: boolean;
}

export interface ReconcileTransactionsRequest {
  type: typeof types.RECONCILE_TRANSACTIONS.REQUEST;
  payload: {
    clientIntegrationId: string;
    startTime: string;
    endTime: string;
    identifyOnly: boolean;
  };
}

export interface ReconcileTransactionsSuccess {
  type: typeof types.RECONCILE_TRANSACTIONS.SUCCESS;
}

export interface ReconcileTransactionsError {
  type: typeof types.RECONCILE_TRANSACTIONS.ERROR;
  payload: string;
}

export interface ResetExportData {
  type: typeof types.RESET_EXPORT_DATA;
}

export interface ResetSalesByCardTypeReport {
  type: typeof types.RESET_SALES_BY_CARD_TYPE_REPORT;
}

export interface ResetSalesReport {
  type: typeof types.RESET_SALES_REPORT;
}

export interface ResetSettlementReport {
  type: typeof types.RESET_SETTLEMENT_REPORT;
}

export type DeliveryLogActionTypes =
  | SearchTransactionsRequest
  | SearchTransactionsSuccess
  | SearchTransactionsError
  | SearchTransactionsPending
  | ResetDeliveryLog
  | GetTransactionPriceTypesRequest
  | GetTransactionPriceTypesSuccess
  | GetTransactionPriceTypesPending
  | RefundTransactionSuccess
  | VoidTransactionSuccess
  | ReconcileTransactionsRequest
  | ReconcileTransactionsSuccess
  | ReconcileTransactionsError
  | GetSettlementReportRequest
  | GetSettlementReportSuccess
  | GetSettlementReportPending
  | GetSettlementReportError
  | GetSalesReportRequest
  | GetSalesReportSuccess
  | GetSalesReportPending
  | GetSalesReportError
  | GetSalesByCardTypeReportRequest
  | GetSalesByCardTypeReportSuccess
  | GetSalesByCardTypeReportPending
  | GetSalesByCardTypeReportError
  | ResetExportData
  | ResetSalesByCardTypeReport
  | ResetSalesReport
  | ResetSettlementReport;
